import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Trophy, ArrowLeft, Info, ChevronUp, ChevronDown, Minus } from 'lucide-react';
import { PlayerProfileModal } from './PlayerProfileModal';
import { TiebreakerModal } from './TiebreakerModal';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import './RankingTournament.css';

export const ClassificationView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState<'standings' | 'competitions'>('standings');

  const [classification, setClassification] = useState<any>(null);
  const [standings, setStandings] = useState<any[]>([]);
  const [competitions, setCompetitions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);
  const [selectedTiebreaker, setSelectedTiebreaker] = useState<any>(null);

  const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";
  const token = localStorage.getItem('tt_auth_token');

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    
    Promise.all([
      fetch(`${API_URL}/api/classifications/${id}`, { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.json()),
      fetch(`${API_URL}/api/classifications/${id}/standings`, { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.json()),
      fetch(`${API_URL}/api/classifications/${id}/competitions`, { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.json())
    ])
    .then(([classData, standingsData, compsData]) => {
      setClassification(classData);
      setStandings(standingsData);
      setCompetitions(compsData);
      setLoading(false);
    })
    .catch(err => {
      console.error(err);
      toast.error('Erro ao carregar classificação');
      setLoading(false);
    });
  }, [id, token, API_URL]);

  const rankingComp = competitions.find(c => c.type === 'RANKING');
  let isPlayerChallenge = false;
  let challengeWindow = 3;

  let parsedConfig = rankingComp?.config;
  if (typeof parsedConfig === 'string') {
    try {
      parsedConfig = JSON.parse(parsedConfig);
    } catch (e) {
      console.error('Failed to parse rankingComp config', e);
    }
  }

  if (parsedConfig) {
    if (parsedConfig.categoriesConfig) {
      for (const catName in parsedConfig.categoriesConfig) {
        const catConfig = parsedConfig.categoriesConfig[catName];
        if (catConfig.ladderRules?.matchGenerationMode === 'PLAYER_CHALLENGE') {
          isPlayerChallenge = true;
          challengeWindow = catConfig.ladderRules?.challengeWindowAbove ?? 3;
          break;
        }
      }
    }
    if (!isPlayerChallenge && parsedConfig.ladderRules?.matchGenerationMode === 'PLAYER_CHALLENGE') {
      isPlayerChallenge = true;
      challengeWindow = parsedConfig.ladderRules.challengeWindowAbove ?? 3;
    }
  }


  const currentUserIndex = standings.findIndex(p => p.userId === currentUser?.id);

  const canChallenge = (targetPlayerId: string) => {
    if (!isPlayerChallenge || currentUserIndex === -1) return false;
    const targetIndex = standings.findIndex(p => p.userId === targetPlayerId);
    if (targetIndex === -1 || targetIndex === currentUserIndex) return false;
    if (challengeWindow === 0) return true;
    return Math.abs(currentUserIndex - targetIndex) <= challengeWindow;
  };

  const handleChallenge = async (targetUserId: string) => {
    if (!rankingComp) {
      toast.error('Nenhum ranking contínuo associado a esta classificação.');
      return;
    }
    if (!window.confirm('Deseja desafiar este jogador?')) return;
    try {
      const res = await fetch(`${API_URL}/api/competitions/${rankingComp.id}/challenges`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ targetUserId })
      });
      if (!res.ok) {
        let errorMessage = 'Falha ao enviar desafio.';
        try {
          const errorData = await res.json();
          if (errorData.error) errorMessage = errorData.error;
        } catch(e) {}
        throw new Error(errorMessage);
      }
      toast.success('Desafio enviado com sucesso! Aguarde a aprovação do adversário.');
    } catch(err: any) {
      toast.error(err.message || 'Falha ao enviar desafio.');
    }
  };

  return (
    <div className="rt-container">
      <div className="rt-card">
        
        {/* Header */}
        <div className="rt-header">
          <div className="rt-header-title" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button onClick={() => navigate(-1)} style={{ background: 'var(--bg-panel)', border: 'none', width: '48px', height: '48px', borderRadius: '50%', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} title="Voltar">
              <ArrowLeft size={24} />
            </button>
            <div style={{ background: 'var(--primary-color)', padding: '12px', borderRadius: '12px', display: 'flex' }}>
              <Trophy size={24} color="#2D1E4B" />
            </div>
            <h1>{loading ? 'Carregando...' : classification?.name || 'Classificação Geral'}</h1>
          </div>
        </div>

        {/* Tabs */}
        <div className="rt-tabs">
          <button 
            className={"rt-tab " + (activeTab === 'standings' ? 'active' : '')}
            onClick={() => setActiveTab('standings')}
          >
            Classificação
          </button>
          <button 
            className={"rt-tab " + (activeTab === 'competitions' ? 'active' : '')}
            onClick={() => setActiveTab('competitions')}
          >
            Competições Associadas
          </button>
        </div>

        {/* Content */}
        <div className="rt-content" style={{ padding: '24px' }}>
          
          {activeTab === 'standings' && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              {standings.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.6)' }}>
                  Nenhum jogador pontuou ainda nesta classificação.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', alignItems: 'center' }}>
                    <div style={{ width: '40px' }}>Pos</div>
                    <div style={{ flex: 1 }}>Jogador</div>
                    <div style={{ width: '32px', textAlign: 'center' }} title="Jogos">J</div>
                    <div style={{ width: '32px', textAlign: 'center' }} title="Vitórias">V</div>
                    <div style={{ width: '32px', textAlign: 'center' }} title="Derrotas">D</div>
                    <div style={{ width: '40px', textAlign: 'center' }} title="Saldo (Vitórias - Derrotas)">SV</div>
                    <div style={{ width: '40px', textAlign: 'center' }} title="Aproveitamento">AP%</div>
                    <div style={{ width: '60px', textAlign: 'right' }}>Pontos</div>
                    {isPlayerChallenge && currentUserIndex !== -1 && <div style={{ width: '80px', textAlign: 'center', marginLeft: '12px' }}>Ação</div>}
                  </div>
                  {standings.map((st, index) => {
                    const diffPos = (st.previousPosition && st.position) ? st.previousPosition - st.position : 0;
                    const ap = st.matchesPlayed > 0 ? Math.round((st.matchesWon / st.matchesPlayed) * 100) : 0;
                    return (
                      <div 
                        key={st.userId} 
                        style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', background: 'rgba(255,255,255,0.02)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)' }}
                      >
                        <div style={{ width: '40px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center' }}>
                          <span style={{ fontWeight: 'bold', fontSize: '16px', color: index < 3 ? 'var(--primary-color)' : 'white' }}>{st.position || index + 1}</span>
                          <div style={{ display: 'flex', alignItems: 'center', fontSize: '10px', color: diffPos > 0 ? '#10b981' : diffPos < 0 ? '#ef4444' : '#9ca3af' }}>
                            {diffPos > 0 && <ChevronUp size={12} />}
                            {diffPos < 0 && <ChevronDown size={12} />}
                            {diffPos === 0 && <Minus size={12} />}
                            {diffPos !== 0 && Math.abs(diffPos)}
                          </div>
                        </div>
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }} onClick={() => setSelectedPlayerId(st.userId)}>
                          <div style={{ width: '36px', height: '36px', borderRadius: '18px', background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                            {st.userPhotoUrl ? (
                              <img src={st.userPhotoUrl} alt={st.userName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <span style={{ fontSize: '14px', fontWeight: 'bold', color: 'rgba(255,255,255,0.5)' }}>
                                {st.userName ? (st.userName.trim().split(/\s+/).length === 1 ? st.userName.trim().split(/\s+/)[0].charAt(0).toUpperCase() : (st.userName.trim().split(/\s+/)[0].charAt(0) + st.userName.trim().split(/\s+/)[st.userName.trim().split(/\s+/).length - 1].charAt(0)).toUpperCase()) : '?'}
                              </span>
                            )}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontSize: '14px', fontWeight: 600 }}>{st.userName}</span>
                            {st.defendingPoints > 0 && (
                              <span style={{ fontSize: '10px', color: '#9ca3af' }}>Def: {st.defendingPoints}</span>
                            )}
                          </div>
                        </div>
                        <div style={{ width: '32px', textAlign: 'center', color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>{st.matchesPlayed || (st.matchesWon + st.matchesLost)}</div>
                        <div style={{ width: '32px', textAlign: 'center', color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>{st.matchesWon}</div>
                        <div style={{ width: '32px', textAlign: 'center', color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>{st.matchesLost}</div>
                        <div style={{ width: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.9)', fontSize: '13px', fontWeight: 500 }}>{(st.matchesWon - st.matchesLost) > 0 ? '+' : ''}{st.matchesWon - st.matchesLost}</div>
                        <div style={{ width: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>{ap}%</div>
                        <div style={{ width: '60px', textAlign: 'right', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '6px' }}>
                          <span style={{ color: 'var(--primary-color)', fontSize: '16px', fontWeight: 800 }}>{st.points}</span>
                          <button onClick={(e) => { e.stopPropagation(); setSelectedTiebreaker(st); }} style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.4)', padding: '2px', cursor: 'pointer', display: 'flex' }} title="Critérios de desempate">
                            <Info size={14} />
                          </button>
                        </div>
                        {isPlayerChallenge && currentUserIndex !== -1 && (
                          <div style={{ width: '80px', display: 'flex', justifyContent: 'center', marginLeft: '12px' }}>
                            {st.userId !== currentUser?.id && (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleChallenge(st.userId); }}
                                disabled={!canChallenge(st.userId)}
                                style={{ 
                                  background: canChallenge(st.userId) ? '#3A8E58' : 'rgba(255,255,255,0.1)', 
                                  color: canChallenge(st.userId) ? 'white' : 'rgba(255,255,255,0.3)', 
                                  border: 'none', 
                                  padding: '6px 12px', 
                                  borderRadius: '6px', 
                                  fontSize: '12px', 
                                  fontWeight: 'bold', 
                                  cursor: canChallenge(st.userId) ? 'pointer' : 'not-allowed', 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: '4px' 
                                }}
                              >
                                Desafiar
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </motion.div>
          )}

          {activeTab === 'competitions' && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              {competitions.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.6)' }}>
                  Nenhuma competição associada a esta classificação.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {competitions.map((comp) => (
                    <div key={comp.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px', background: 'rgba(255,255,255,0.02)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
                      <div>
                        <h4 style={{ margin: '0 0 4px 0', fontSize: '16px' }}>{comp.name}</h4>
                        <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>
                          {comp.type === 'RANKING' ? 'Ranking Contínuo' : 'Torneio'} • {comp.status === 'ONGOING' ? 'Em andamento' : comp.status === 'FINISHED' ? 'Finalizado' : 'Inativo'}
                        </span>
                      </div>
                      <button 
                        onClick={() => navigate(comp.type === 'RANKING' ? `/rankings/${comp.id}` : `/tournaments/${comp.id}`)}
                        style={{ background: 'rgba(255,255,255,0.1)', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px' }}
                      >
                        Ver Detalhes
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          )}

        </div>
      </div>

      {selectedPlayerId && (
        <PlayerProfileModal 
          playerId={selectedPlayerId} 
          onClose={() => setSelectedPlayerId(null)} 
        />
      )}

      {selectedTiebreaker && (
        <TiebreakerModal
          isOpen={!!selectedTiebreaker}
          onClose={() => setSelectedTiebreaker(null)}
          playerData={selectedTiebreaker}
        />
      )}
    </div>
  );
};
